import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  LinearProgress,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  Business as BusinessIcon,
  CreditCard as SubscriptionIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  FreeBreakfast as FreeTrialIcon,
  Block as InactiveIcon,
} from '@mui/icons-material';
import SuperAdminLayout from '../components/SuperAdminLayout';
import superAdminDashboardService from '../services/superAdminDashboardService';
import { toast } from 'react-hot-toast';

/**
 * Super Admin Dashboard
 * Main dashboard for super administrators
 */
const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expiryPage, setExpiryPage] = useState(0);
  const [expiryRowsPerPage, setExpiryRowsPerPage] = useState(10);
  const [stats, setStats] = useState({
    totalClients: 0,
    totalPlans: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    totalFreeTrials: 0,
    inactiveCompanies: 0,
    expiryMonth: '',
    expiringClients: [],
  });

  useEffect(() => {
    const fetchDashboardStatistics = async () => {
      try {
        setLoading(true);
        const data = await superAdminDashboardService.getDashboardStatistics();
        setStats({
          totalClients: data.totalClients || 0,
          totalPlans: data.totalPlans || 0,
          activeSubscriptions: data.activeSubscriptions || 0,
          totalRevenue: data.totalRevenue || 0,
          totalFreeTrials: data.totalFreeTrials || 0,
          inactiveCompanies: data.inactiveCompanies || 0,
          expiryMonth: data.expiryMonth || '',
          expiringClients: data.expiringClients || [],
        });
      } catch (error) {
        setLoadError(true);
        console.error('Error fetching dashboard statistics:', error);
        toast.error('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStatistics();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const statCards = [
    {
      title: 'Total Clients',
      value: loading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : stats.totalClients,
      icon: BusinessIcon,
      color: '#2196F3',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/superadmin/clients'),
    },
    {
      title: 'Total Plans',
      value: loading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : stats.totalPlans,
      icon: SubscriptionIcon,
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/superadmin/subscriptions'),
    },
    {
      title: 'Active Subscriptions',
      value: loading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : stats.activeSubscriptions,
      icon: PeopleIcon,
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
    },
    {
      title: 'Total Revenue',
      value: loading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : formatCurrency(stats.totalRevenue),
      icon: TrendingUpIcon,
      color: '#00BCD4',
      gradient: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
    },
    {
      title: 'Total Free Trials',
      value: loading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : stats.totalFreeTrials,
      icon: FreeTrialIcon,
      color: '#4CAF50',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
    },
    {
      title: 'Inactive Companies',
      value: loading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : stats.inactiveCompanies,
      icon: InactiveIcon,
      color: '#F44336',
      gradient: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
    },
  ];

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: '#2c3e50' }}>
            Super Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome to the Tiya Technology Administration Panel
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        <Grid container spacing={3}>
          {statCards.map((kpi, index) => {
            // Adjust grid size for 6 cards: 2 per row on large screens, 1 per row on small screens
            const gridSize = { xs: 12, sm: 6, md: 4, lg: 4 };
            const IconComponent = kpi.icon;
            return (
              <Grid item {...gridSize} key={index}>
                <Paper 
                  elevation={0} 
                  onClick={kpi.onClick || undefined}
                  sx={{ 
                    p: 2.5,
                    height: '100%',
                    minHeight: 140,
                    background: kpi.gradient,
                    border: 'none',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: kpi.onClick ? 'pointer' : 'default',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      '& .card-icon': {
                        transform: 'scale(1.1)',
                        opacity: 1
                      },
                      '& .card-content': {
                        transform: 'translateY(-5px)'
                      }
                    }
                  }}
                >
                  <Box 
                    className="card-content"
                    sx={{ 
                      transition: 'transform 0.3s ease',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            color: kpi.textColor,
                            fontWeight: 500,
                            mb: 0.5,
                            opacity: 0.9
                          }}
                        >
                          {kpi.title}
                        </Typography>
                        <Typography 
                          variant="h5" 
                          component="div" 
                          sx={{ 
                            mb: 1,
                            fontWeight: 600,
                            color: kpi.textColor,
                            fontSize: { xs: '1.5rem', sm: '1.75rem' }
                          }}
                        >
                          {kpi.value}
                        </Typography>
                      </Box>
                      <Box
                        className="card-icon"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 56,
                          height: 56,
                          borderRadius: '12px',
                          backgroundColor: kpi.iconBg,
                          transition: 'all 0.3s ease',
                          opacity: 0.9,
                          ml: 1
                        }}
                      >
                        <IconComponent 
                          sx={{ 
                            fontSize: 28, 
                            color: kpi.textColor 
                          }} 
                        />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <Paper elevation={0} sx={{ mt: 4, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Clients expiring this month{!loading && !loadError ? ` (${stats.expiringClients.length})` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stats.expiryMonth && new Date(`${stats.expiryMonth}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
              {stats.expiryMonth ? ' — ' : ''}Subscription and trial expiries, including clients already expired this month.
            </Typography>
          </Box>
          {loading ? <LinearProgress aria-label="Loading expiring clients" /> : loadError ? (
            <Alert severity="error" sx={{ mx: 3, mb: 3 }}>Could not load client expiries. Please refresh the dashboard.</Alert>
          ) : stats.expiringClients.length === 0 ? (
            <Alert severity="info" sx={{ mx: 3, mb: 3 }}>No clients have a subscription or trial expiring this month.</Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small" aria-label="Clients expiring this month" sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow>
                      {['Client', 'Contact', 'Plan', 'Type', 'Expiry date', 'Time remaining'].map(label => (
                        <TableCell key={label} sx={{ fontWeight: 600 }}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.expiringClients.slice(expiryPage * expiryRowsPerPage, (expiryPage + 1) * expiryRowsPerPage).map(client => (
                      <TableRow key={client.companyId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{client.companyName}</Typography>
                          <Typography variant="caption" color="text.secondary">ID: {client.companyId}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{client.phone || '—'}</Typography>
                          <Typography variant="body2" color="text.secondary">{client.email || '—'}</Typography>
                        </TableCell>
                        <TableCell>{client.planName || '—'}</TableCell>
                        <TableCell>{client.expiryType}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(client.expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={client.expired ? 'error' : client.daysRemaining <= 7 ? 'warning' : 'success'}
                            label={client.expired ? 'Expired' : client.daysRemaining === 0 ? 'Expires today' : `${client.daysRemaining} day${client.daysRemaining === 1 ? '' : 's'} left`} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={stats.expiringClients.length} page={expiryPage}
                rowsPerPage={expiryRowsPerPage} rowsPerPageOptions={[10, 25, 50]}
                onPageChange={(_event, page) => setExpiryPage(page)}
                onRowsPerPageChange={event => { setExpiryRowsPerPage(parseInt(event.target.value, 10)); setExpiryPage(0); }} />
            </>
          )}
        </Paper>
      </Box>
    </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;

